if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();

        this.selectors = {
          viewer: '[id^="GalleryViewer"]',
          thumbnails: '[id^="GalleryThumbnails"]',
          mediaList: '[id^="Slider-Gallery"]',
          thumbnailList: '[id^="Slider-Thumbnails"]',
          mediaItems: ['.product__media-item'],
          thumbItems: ['.product__thumbs-item'],
        };

        this.initialized = false;
        this.sliderInstance = false;
        this.thumbsInstance = false;
      }

      connectedCallback() {
        FoxTheme.Motion.inView(this, this.init.bind(this));
      }

      init() {
        if (this.initialized === true) return;

        this.elements = window.FoxTheme.utils.queryDomNodes(this.selectors, this);

        // Server can render some items pre-hidden (e.g. a ?variant= deep link already
        // filtered by variant) while leaving them in their original position in the DOM.
        // Swiper's autoHeight defaults to slide 0 — if that slide is hidden, the whole
        // viewer collapses to 0 height before the slider ever builds. Strip hidden items
        // out of the live layout up front so Swiper only ever sees a contiguous visible set.
        this.removeHiddenMediaFromDom();

        this.mediaLayout = this.dataset.mediaLayout;
        this.onlyImage = this.dataset.onlyImage === 'true';
        this.enableDesktopSlider = this.dataset.enableDesktopSlider === 'true';
        this.enableMobileThumbnails = this.dataset.enableMobileThumbnails === 'true';
        this.enableImageZoom = this.dataset.enableImageZoom === 'true';
        this.context = this.dataset.context;
        this.setSliderOptions();

        const mql = window.matchMedia(FoxTheme.config.mediaQueryMobile);
        mql.onchange = this.updateMediaLayout.bind(this);
        this.updateMediaLayout();

        if (this.enableImageZoom) {
          this.initImageZoom();
        }

        this.initialized = true;
      }

      setSliderOptions() {
        const mediaItemGap = parseInt(this.dataset.mediaItemGap);
        const mediaItemGapMobile = parseInt(this.dataset.mediaItemGapMobile);

        this.sliderOptions = {
          init: false,
          slidesPerView: this.enableMobileThumbnails ? 1 : 'auto',
          spaceBetween: mediaItemGapMobile,
          loop: false,
          grabCursor: true,
          allowTouchMove: true,
          autoHeight: true,
          breakpoints: {
            768: {
              spaceBetween: mediaItemGap,
            },
          },
          navigation: {
            nextEl: this.querySelector('.swiper-button-next'),
            prevEl: this.querySelector('.swiper-button-prev'),
          },
          pagination: {
            el: this.querySelector('.swiper-pagination'),
            clickable: true,
            type: 'fraction',
          },
          threshold: 2,
        };

        this.thumbsOptions = {
          slidesPerView: 4,
          breakpoints: {
            461: {
              slidesPerView: 5,
              direction: 'horizontal',
            },
          },
          spaceBetween: mediaItemGapMobile,
          loop: false,
          freeMode: true,
          watchSlidesProgress: true,
          threshold: 2,
          // freeMode lets you drag-scroll, but a thumbnail strip taller/wider than its
          // container needs wheel support too — without it the mouse wheel just scrolls
          // the page underneath instead of the strip.
          mousewheel: {
            enabled: true,
            forceToAxis: true,
            releaseOnEdges: true,
          },
          breakpoints: {
            768: {
              spaceBetween: mediaItemGap,
            },
          },
        };

        switch (this.mediaLayout) {
          case 'vertical-carousel':
            this.thumbsOptions.breakpoints = {
              ...this.thumbsOptions.breakpoints,
              768: {
                direction: 'vertical',
                slidesPerView: 'auto',
                spaceBetween: mediaItemGap,
              },
            };
            break;
          case 'carousel':
            this.thumbsOptions.breakpoints = {
              ...this.thumbsOptions.breakpoints,
              1024: {
                slidesPerView: 5,
              },
              1536: {
                slidesPerView: 7,
              },
            };
            break;
        }
      }

      updateMediaLayout() {
        if (FoxTheme.config.mqlMobile) {
          this.initSlider();
        } else {
          if (this.enableDesktopSlider) {
            this.initSlider();
          } else {
            this.destroySlider();
          }
        }
      }

      initSlider() {
        if (typeof this.sliderInstance !== 'object') {
          if ((this.enableDesktopSlider || this.enableMobileThumbnails) && this.elements.thumbnails) {
            this.thumbsInstance = new window.FoxTheme.Carousel(this.elements.thumbnails, this.thumbsOptions, [
              FoxTheme.Swiper.Mousewheel,
            ]);
            this.thumbsInstance.init();

            this.sliderOptions.thumbs = {
              swiper: this.thumbsInstance.slider,
              autoScrollOffset: 2,
            };
          }

          this.sliderInstance = new window.FoxTheme.Carousel(this.elements.viewer, this.sliderOptions, [
            FoxTheme.Swiper.Thumbs,
          ]);
          this.sliderInstance.init();

          this.handleSliderAfterInit();
          this.handleSlideChange();

          this.sliderInstance.slider.init();
        }
      }

      destroySlider() {
        if (typeof this.sliderInstance === 'object') {
          this.sliderInstance.slider.destroy();
          this.sliderInstance = false;
        }
      }

      rebuildSlider() {
        // Repeated update() calls across many variant switches leave Swiper's internal
        // slide-click cache stale, silently breaking thumbnail clicks until a real touch
        // gesture forces Swiper to recompute it. Destroying and recreating both Swiper
        // instances guarantees a clean state on every filter change instead.
        if (typeof this.sliderInstance === 'object') {
          this.sliderInstance.slider.destroy(true, true);
          this.sliderInstance = false;
        }
        if (typeof this.thumbsInstance === 'object') {
          this.thumbsInstance.slider.destroy(true, true);
          this.thumbsInstance = false;
        }
        this.initSlider();
      }

      initThumbsSlider() {
        if (typeof this.thumbsInstance !== 'object') {
          this.thumbsInstance = new window.FoxTheme.Carousel(this.selectors.thumbnails, this.thumbsOptions);
          this.thumbsInstance.init();
        }
      }

      buildLightboxDataSource() {
        const allMedia = [...this.querySelectorAll('.product__media-item:not(.swiper-slide-duplicate)')];

        return allMedia.map((media) => {
          const { mediaType, mediaIndex, src, pswpWidth, pswpHeight } = media.dataset;

          let source = {
            id: mediaIndex,
            mediaType: mediaType,
          };

          switch (mediaType) {
            case 'model':
            case 'video':
            case 'external_video':
              const htmlTag = mediaType === 'model' ? 'product-model' : 'video-element';

              source = {
                ...source,
                html: `<div class="pswp__item--${mediaType}">${media.querySelector(htmlTag).outerHTML}</div>`,
              };
              break;
            default: // Image.
              source = {
                ...source,
                src: src,
                width: pswpWidth,
                height: pswpHeight,
              };
              break;
          }

          return source;
        });
      }

      updateLightboxDataSource() {
        if (!this.lightboxDataSource) return;

        // Filtering by variant can change which media items are in the DOM after the lightbox
        // was built — keep the same array reference (mutate in place) so the lightbox instance,
        // which was constructed with this exact array, stays in sync.
        this.lightboxDataSource.length = 0;
        this.lightboxDataSource.push(...this.buildLightboxDataSource());
      }

      initImageZoom() {
        this.lightboxDataSource = this.buildLightboxDataSource();

        this.lightbox = new window.FoxTheme.PhotoSwipeLightbox({
          dataSource: this.lightboxDataSource,
          pswpModule: window.FoxTheme.PhotoSwipe,
          bgOpacity: 1,
          arrowPrev: false,
          arrowNext: false,
          zoom: false,
          close: false,
          counter: false,
          preloader: false,
        });

        this.lightbox.addFilter('thumbEl', (thumbEl, { id }, index) => {
          const slider = this.sliderInstance?.slider;

          if (slider) {
            const { slides, activeIndex } = slider;
            if (slides[activeIndex]) {
              const el = slides[activeIndex].querySelector('img');
              if (el) {
                return el;
              }
            }
          }

          return thumbEl;
        });

        this.lightbox.addFilter('placeholderSrc', (placeholderSrc, { data: { id } }) => {
          const slider = this.sliderInstance?.slider;

          if (slider) {
            const { slides, activeIndex } = slider;
            if (slides[activeIndex]) {
              const el = slides[activeIndex].querySelector('img');
              if (el) {
                return el.src;
              }
            }
          }

          return placeholderSrc;
        });

        this.lightbox.on('change', () => {
          window.pauseAllMedia(this);
        });

        // Store the current index before closing
        let lastLightboxIndex = null;
        this.lightbox.on('close', () => {
          if (this.lightbox?.pswp?.currIndex) {
            lastLightboxIndex = this.lightbox.pswp.currIndex;
          }
        });

        // Update slider after lightbox is destroyed
        this.lightbox.on('destroy', () => {
          const slider = this.sliderInstance?.slider;

          if (slider && lastLightboxIndex !== null) {
            const targetIndex = lastLightboxIndex;
            // currIndex from lightbox is the index in dataSource array
            // which should match the slider index since both are created from the same media items in the same order
            if (targetIndex >= 0 && targetIndex < slider.slides.length && slider.activeIndex !== targetIndex) {
              // Use requestAnimationFrame to ensure DOM is ready
              requestAnimationFrame(() => {
                slider.slideTo(targetIndex, 0);
              });
            }
            lastLightboxIndex = null;
          }
        });

        this.lightbox.on('pointerDown', (e) => {
          if (this.lightbox?.pswp?.currSlide?.data?.mediaType != 'image') {
            e.preventDefault();
          }
        });

        this.lightbox.on('uiRegister', () => {
          const lightboxUI = this.lightbox?.pswp?.ui;

          if (!lightboxUI) return;

          if (!this.onlyImage) {
            lightboxUI.registerElement({
              name: 'next',
              ariaLabel: 'Next slide',
              order: 3,
              isButton: true,
              html: '<svg class="pswp-icon-next flip-x" viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z"></path></svg>',
              onClick: (event, el) => {
                this.lightbox?.pswp?.next();
              },
            });
            lightboxUI.registerElement({
              name: 'prev',
              ariaLabel: 'Previous slide',
              order: 1,
              isButton: true,
              html: '<svg class="pswp-icon-prev rtl-flip-x" viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z"></path></svg>',
              onClick: (event, el) => {
                this.lightbox?.pswp?.prev();
              },
            });
          }
          lightboxUI.registerElement({
            name: 'close-zoom',
            ariaLabel: 'Close zoom image',
            order: 2,
            isButton: true,
            html: '<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" role="presentation" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>',
            onClick: (event, el) => {
              this.lightbox?.pswp?.close();
            },
          });
        });

        this.lightbox.init();

        FoxTheme.utils.addEventDelegate({
          selector: '.js-photoswipe--zoom',
          context: this,
          handler: (e, media) => {
            if (media.dataset?.mediaType === 'image') {
              // `media` is the `.js-photoswipe--zoom` button itself, not its `.product__media-item`
              // ancestor — resolve the ancestor before indexing, or indexOf never matches.
              const mediaItem = media.closest('.product__media-item');
              // Index into the lightbox's dataSource by current DOM position, not the media
              // item's static `data-media-index` — variant filtering can remove/reorder items
              // so that position no longer matches the (possibly filtered) dataSource array.
              const visibleMedia = [...this.querySelectorAll('.product__media-item:not(.swiper-slide-duplicate)')];
              const index = visibleMedia.indexOf(mediaItem);
              if (index >= 0) this.lightbox.loadAndOpen(index);
            }
          },
        });
      }

      handleSliderAfterInit() {
        this.sliderInstance.slider.on('afterInit', (swiper) => {
          const { slides, activeIndex } = swiper;

          if (slides[activeIndex]) {
            const isModelMediaType = slides[activeIndex].dataset.mediaType === 'model';
            this.toggleSliderDraggableState(!isModelMediaType);
          }
        });
      }

      handleSlideChange() {
        this.sliderInstance.slider.on('realIndexChange', (swiper) => {
          const { slides, activeIndex, thumbs } = swiper;

          if (thumbs.swiper) {
            thumbs.swiper.slideTo(activeIndex);
          }

          if (slides[activeIndex]) {
            this.playActiveMedia(slides[activeIndex]);

            const isModelMediaType = slides[activeIndex].dataset.mediaType === 'model';
            this.toggleSliderDraggableState(!isModelMediaType);
          }
        });
      }

      toggleSliderDraggableState(isDraggable) {
        if (this.sliderInstance.slider.allowTouchMove !== isDraggable) {
          this.sliderInstance.slider.allowTouchMove = isDraggable;
        }
      }

      playActiveMedia(selected) {
        const deferredMedia = selected.querySelector('product-model');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      removeHiddenMediaFromDom() {
        const allItems = this.elements.mediaItems;
        if (!allItems?.length || !allItems.some((item) => item.hidden)) return;

        const visibleItems = allItems.filter((item) => !item.hidden);
        this.elements.mediaList.innerHTML = '';
        visibleItems.forEach((item) => this.elements.mediaList.appendChild(item));

        if (this.elements.thumbnailList && this.elements.thumbItems?.length) {
          this.elements.thumbnailList.innerHTML = '';
          this.elements.thumbItems
            .filter((item) => !item.hidden)
            .forEach((item) => this.elements.thumbnailList.appendChild(item));
        }
      }

      filterMediaByVariant(variant) {
        const allItems = this.elements.mediaItems;
        if (!variant?.title || !allItems?.length) return;

        // Alt text tags an image by its option values joined with "," (e.g. "Black,6'"),
        // not `variant.title` (e.g. "Black / 6'") — comparing against title would never
        // match for any product with more than one option.
        const variantTag = variant.options ? variant.options.join(',') : variant.title;

        const matched = allItems.filter((item) => {
          const alt = item.dataset.mediaAlt;
          if (!alt) return false;

          // Alt text tags an image as "<variant tag> |#| <description>" — match the tag
          // exactly so e.g. a "Red" variant doesn't also pull in "Red Light" media. Fall back
          // to substring matching only for untagged alt text with no delimiter present.
          if (alt.includes('|#|')) {
            return alt.split('|#|')[0].trim() === variantTag;
          }
          return alt.includes(variant.title);
        });

        // No tagged images, or every image matches: nothing to filter, leave gallery untouched.
        const visibleItems = matched.length && matched.length < allItems.length ? matched : allItems;
        const visibleIds = new Set(visibleItems.map((item) => item.dataset.mediaId));

        // Server-side render may have marked some items `hidden` (e.g. a ?variant= deep link) —
        // clear that explicitly for whatever we decide is visible now, regardless of past state.
        this.elements.mediaList.innerHTML = '';
        visibleItems.forEach((item) => {
          item.hidden = false;
          this.elements.mediaList.appendChild(item);
        });

        if (this.elements.thumbnailList && this.elements.thumbItems?.length) {
          this.elements.thumbnailList.innerHTML = '';
          this.elements.thumbItems
            .filter((item) => visibleIds.has(item.dataset.target))
            .forEach((item) => {
              item.hidden = false;
              this.elements.thumbnailList.appendChild(item);
            });
        }
      }

      setActiveMedia(variant, filterByVariant = false) {
        if (!variant) return;

        // Initial page load already renders filtered server-side (product-media-gallery.liquid
        // uses the same selected_or_first_available_variant), so the JS side only needs to
        // filter again when the caller tells us this came from an explicit option change.
        if (filterByVariant) {
          this.filterMediaByVariant(variant);
          this.updateLightboxDataSource();
        }

        let featuredMedia = variant.featured_media;

        if (!featuredMedia && variant.featured_image) {
          featuredMedia = {
            id: variant.featured_image.id,
            position: variant.featured_image.position || 0,
            media_type: 'image',
          };
        }

        if (this.sliderInstance.slider) {
          // Always destroy and rebuild both the main and thumbs Swiper instances on every
          // variant change — relying on update() to keep them in sync across many changes
          // has proven unreliable (stale slide-click cache, drifted thumb positions). Rebuild
          // even when this variant has no featured_media/featured_image: filterMediaByVariant()
          // above may already have changed the live slide count, and skipping the rebuild would
          // leave Swiper's slides stale against the filtered DOM (broken clicks, blank viewer).
          this.rebuildSlider();

          if (!featuredMedia) return;

          const visibleSlides = [...this.elements.mediaList.children];
          const slideIndex = visibleSlides.findIndex((el) => el.dataset.mediaId == featuredMedia.id);
          const targetIndex = slideIndex >= 0 ? slideIndex : 0;

          // slideTo's 3rd arg (runCallbacks) is false here, so Swiper's own 'realIndexChange'
          // listener — the one that normally keeps the thumbnail strip's scroll position in
          // sync with the main slide — never fires. Sync it explicitly instead.
          this.sliderInstance.slider.slideTo(targetIndex, 0, false);
          this.thumbsInstance?.slider?.slideTo(targetIndex, 0, false);
        } else {
          if (!featuredMedia) return;
          this.sortMediaItems(variant, featuredMedia);
        }
      }

      sortMediaItems(variant, featuredMedia) {
        if (!featuredMedia) {
          featuredMedia =
            variant.featured_media ||
            (variant.featured_image
              ? {
                  id: variant.featured_image.id,
                  position: variant.featured_image.position || 0,
                  media_type: 'image',
                }
              : null);
        }

        if (!featuredMedia) return;

        // Reordering happens on the already-filtered slide list, not the full master list.
        let newMedias = [...this.elements.mediaList.children];

        // Reset ordering.
        newMedias.sort(function (a, b) {
          return a.dataset.mediaIndex - b.dataset.mediaIndex;
        });

        newMedias.some((media, index) => {
          if (media.dataset.mediaId == featuredMedia.id) {
            const [element] = newMedias.splice(index, 1);
            newMedias.unshift(element);
            return true;
          }
        });

        this.elements.mediaList.innerHTML = '';
        newMedias.forEach((media) => {
          this.elements.mediaList.appendChild(media);
        });

        if (!FoxTheme.config.mqlMobile && this.context !== 'quickview') {
          const selectedMedia = this.querySelector(`[data-media-id="${featuredMedia.id}"]`);
          if (selectedMedia) {
            window.scrollTo({ top: selectedMedia.offsetTop, behavior: 'smooth' });
          }
        }
      }
    }
  );
}
